<template>
  <div class="min-h-screen bg-gray-50">
    <HeaderDefault />

    <!-- Hero Section -->
    <section class="bg-gradient-to-b from-primary to-primary/70 py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-3xl">
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-6">
            Transforming Academic Discovery
          </h1>
          <p class="text-xl text-primary-light leading-relaxed">
            Empowering academics worldwide with curated events and meaningful
            networking opportunities.
          </p>
        </div>
      </div>
    </section>

    <!-- Mission Section -->
    <section class="py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-3xl">
          <h2 class="text-3xl font-bold text-gray-900 mb-8">Our Mission</h2>
          <div class="space-y-6 text-gray-600">
            <p class="text-lg leading-relaxed">
              Academic Alerts bridges the gap between like-minded professionals,
              conference organizers, and speakers, offering a seamless platform
              to foster meaningful connections and spark innovation.
            </p>
            <p class="text-lg leading-relaxed">
              We provide accurate, timely information on academic events
              worldwide, empowering users to explore global opportunities
              effortlessly. Our platform transcends boundaries, creating a
              vibrant community dedicated to intellectual growth and
              collaboration.
            </p>
          </div>
        </div>
      </div>
    </section>




    <!-- Incubation Section -->
    <section class="py-16 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-3xl mx-auto">
          <div
            class="bg-white rounded-2xl shadow-sm p-8 flex flex-col sm:flex-row items-center gap-6"
          >
            <img
              src="../../assets/images/mutbi.png"
              alt="Manipal Universal Technology Business Incubator"
              class="h-20 w-auto"
            />
            <div>
              <p class="text-sm text-gray-600 mb-2">Incubated at</p>
              <h3 class="text-xl font-semibold text-gray-900">
                Manipal Universal Technology Business Incubator
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>
    <UStats class="mb-20"></UStats>

    <!-- Team Section -->
    <section class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Founders -->
        <div class="mb-20">
          <h2 class="text-3xl font-bold text-center mb-12">
            Meet Our Founders
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div
              v-for="founder in founders"
              :key="founder.name"
              class="bg-gray-50 rounded-xl p-6 text-center transform hover:scale-105 transition-transform"
            >
              <img
                :src="founder.image"
                :alt="founder.name"
                class="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-white shadow-md"
              />
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                {{ founder.name }}
              </h3>
              <p class="text-primary font-medium mb-3">{{ founder.role }}</p>
              <!-- <p class="text-gray-600 text-sm">{{ founder.bio }}</p> -->
            </div>
          </div>
        </div>

        <!-- Advisors -->
        <div>
          <h2 class="text-3xl font-bold text-center mb-12">Our Advisors</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div
              v-for="advisor in advisors"
              :key="advisor.name"
              class="bg-gray-50 rounded-xl p-6 text-center transform hover:scale-105 transition-transform"
            >
              <img
                :src="advisor.image"
                :alt="advisor.name"
                class="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-white shadow-md"
              />
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                {{ advisor.name }}
              </h3>
              <p class="text-primary font-medium mb-3">{{ advisor.role }}</p>
              <p class="text-gray-600 text-sm">{{ advisor.bio }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="bg-primary py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-3xl font-bold text-white mb-6">
          Ready to Join Our Community?
        </h2>
        <p class="text-xl text-primary-light mb-8">
          Discover academic events and connect with peers worldwide
        </p>
        <NuxtLink
          to="/become-a-member"
          class="inline-flex items-center px-6 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-gray-50 transition-colors"
        >
         Become a Member
          <icon name="heroicons:arrow-right" class="w-5 h-5 ml-2" />
        </NuxtLink>
      </div>
    </section>

    <FooterDefault />
  </div>
</template>

<script setup lang="ts">
const founders = [
  {
    name: "Dr Ravikiran Ongole",
    role: "CEO & Co-founder",
    image: "/about/ravi.png",
    // bio: "Former Medical Director with 15 years of experience in healthcare technology",
  },
  {
    name: "Dr Joanna Baptist",
    role: "COO & Co-founder",
    // bio: "Healthcare informatics expert with focus on AI and machine learning",

    image: "/about/joana.png",
  },
  {
    name: "Sarabpreet",
    role: "CTO & Co-founder",
    // bio: "Full Stack Developer with a knack of building scalable web applications",
    image: "/about/sarabpreet.png",
  },
];

const advisors = [
  {
    name: "Dr. Animesh Jain",
    role: "Strategic Advisor",
    bio: "Professor at the Department of Community Medicine, Kasturba Medical College, Mangalore",
    image: "/about/animesh.png",
  },
  {
    name: "Dr. Mohammad Zuber",
    role: "Strategic Advisor",
    bio: "Chief Innovation Officer, MAHE Manipal",
    image: "/about/zubair.png",
  },
];

useHead({
  title: "About Us - Academic Alerts",
  meta: [
    {
      name: "description",
      content:
        "Learn about Academic Alerts - your platform for discovering academic events and connecting with peers worldwide.",
    },
  ],
});
</script>

<style></style>
